import { ModalityType } from '../generated-interfaces/graphql';
import { ParsedModifierGroup } from '../types/menu';
import { isSuppressedUpsell } from './groupedUpsellDeclined';
import logger from './logger';
import { CartItem, selectModifierInCartItem } from './cart';
import { DO_NOT_UPSELL_IN_EXPRESS_MODE } from './menuUnification.constants';

export interface UpsellConditionContext {
  cartItem: CartItem;
  ancestorNames: string[];
}

export interface UpsellConditionHandler {
  name: string;
  shouldAutoDecline(
    mg: ParsedModifierGroup,
    ctx: UpsellConditionContext
  ): boolean;
}

export const suppressUpsellHandler: UpsellConditionHandler = {
  name: 'SUPPRESS_UPSELL',
  shouldAutoDecline: (mg) => isSuppressedUpsell(mg.suppress_upsell_conditions),
};

// Express mode declines only the constraints the menu explicitly tags with
// `do_not_upsell_in_express_mode` — never untagged upsell prompts (HITL-1509).
// This is the single handler for both ways of entering express mode: the
// express meal period and the elapsed hitl_express_timer.
export const expressModeHandler: UpsellConditionHandler = {
  name: 'EXPRESS_MODE',
  shouldAutoDecline: (mg) => !!mg.do_not_upsell_in_express_mode,
};

// Is this a prompt express mode must never ask? Both fields are required: the
// tag alone is inert on a group the menu does not model as an upsell prompt,
// and every express path (the auto-decline above, the omnibar auto-prompt
// filter, the mod group heading click) gates on the same pair (HITL-1529).
export const isExpressSuppressedUpsellPrompt = (
  modGroup: Pick<
    ParsedModifierGroup,
    'is_upsell_prompt' | 'do_not_upsell_in_express_mode'
  >,
  isExpressMode: boolean
): boolean =>
  isExpressMode &&
  !!modGroup.is_upsell_prompt &&
  !!modGroup.do_not_upsell_in_express_mode;

export const excludedItemHandler: UpsellConditionHandler = {
  name: 'UPSELL_EXCLUDED_ITEM',
  shouldAutoDecline: (mg, ctx) => {
    if (!mg.upsell_excluded_items?.length) return false;
    const excluded = new Set(mg.upsell_excluded_items);
    return ctx.ancestorNames.some((a) => excluded.has(a));
  },
};

export const applyUpsellConditionsToCartItem = (
  cartItem: CartItem,
  modality: ModalityType,
  handlers: UpsellConditionHandler[],
  ancestorNames: string[] = []
): void => {
  if (handlers.length === 0) return;

  const currentAncestors = [...ancestorNames, cartItem.name?.toLowerCase()];
  const ctx: UpsellConditionContext = {
    cartItem,
    ancestorNames: currentAncestors,
  };

  Object.values(cartItem.modifierGroups ?? {}).forEach((mg) => {
    if (!mg.is_upsell_prompt) return;

    const matchedHandlers = handlers.filter((h) =>
      h.shouldAutoDecline(mg, ctx)
    );
    if (matchedHandlers.length === 0) return;

    const hasSelection =
      Object.keys(cartItem.childModifierGroups?.[mg.id]?.selectedItems ?? {})
        .length > 0;
    if (hasSelection) return;

    const declineItem = mg.upsell_declined_item
      ? Object.values(mg.menuItems ?? {}).find(
          (item) => item.name === mg.upsell_declined_item
        )
      : undefined;
    const handlerNames = matchedHandlers.map((h) => h.name).join(', ');
    if (!declineItem) {
      return;
    }

    selectModifierInCartItem(
      cartItem,
      declineItem.itemId,
      mg.id,
      true,
      '',
      {},
      {},
      modality,
      undefined,
      undefined,
      false
    );
    logger.debug(
      `[${handlerNames}] auto-declined "${declineItem.name}" in modGroup "${mg.name}" of cart item "${cartItem.name}" (cartItemId=${cartItem.cartItemId})`
    );
  });

  Object.values(cartItem.childModifierGroups ?? {}).forEach((childGroup) => {
    Object.values(childGroup.selectedItems ?? {}).forEach((nestedItem) => {
      applyUpsellConditionsToCartItem(
        nestedItem,
        modality,
        handlers,
        currentAncestors
      );
    });
  });
};

export interface ParsedUpsellConditions {
  suppress_upsell_conditions: string[];
  do_not_upsell_in_express_mode: boolean;
  upsell_excluded_items: string[];
}

export const parseUpsellConditions = (
  flattenedConditions: string[]
): ParsedUpsellConditions => ({
  suppress_upsell_conditions: flattenedConditions.filter((c) =>
    /^suppress_upsell\.\w+$/.test(c)
  ),
  do_not_upsell_in_express_mode: flattenedConditions.includes(
    DO_NOT_UPSELL_IN_EXPRESS_MODE
  ),
  upsell_excluded_items: flattenedConditions
    .filter((c) => /^upsell_excluded_item:/i.test(c))
    .flatMap((c) =>
      c
        .replace(/^upsell_excluded_item:/i, '')
        .split(',')
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean)
    ),
});
